import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useWorkspace } from './WorkspaceContext';
import { featureFlagApi } from '../services/api';

const FeatureFlagContext = createContext(null);

export const useFeatureFlags = () => {
    const context = useContext(FeatureFlagContext);
    if (!context) {
        throw new Error('useFeatureFlags must be used within a FeatureFlagProvider');
    }
    return context;
};

export const FeatureFlagProvider = ({ children }) => {
    const { isAuthenticated } = useAuth();
    const { currentWorkspace } = useWorkspace();
    const [flags, setFlags] = useState({});
    const [loading, setLoading] = useState(true);

    const fetchFlags = useCallback(async () => {
        if (!isAuthenticated) {
            setFlags({});
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const workspaceId = currentWorkspace?.id;
            const response = await featureFlagApi.getFeatureFlags(workspaceId);
            
            if (response.data && response.data.success) {
                setFlags(response.data.flags);
            }
        } catch (error) {
            console.error('Failed to fetch feature flags:', error);
            // Default to empty flags on failure to prevent app breakage
            setFlags({});
        } finally {
            setLoading(false);
        }
    }, [isAuthenticated, currentWorkspace?.id]);

    useEffect(() => {
        fetchFlags();
    }, [fetchFlags]);

    const isFeatureEnabled = (key) => {
        return !!flags[key];
    };

    const value = {
        flags,
        loading,
        isFeatureEnabled,
        refetchFlags: fetchFlags
    };

    return (
        <FeatureFlagContext.Provider value={value}>
            {children}
        </FeatureFlagContext.Provider>
    );
};
